import { useMemo } from 'react'
import useSWR from 'swr'

import { backendFetchResultData, Paginator } from '../common/api'
import { BulkAction } from '../common/bulk-actions'
import { anyWindow, useQuery } from '../common/helpers'
import { subscriptionListEmptyData } from '../common/new-user-test-data'
import { SubscriptionContract } from '../common/subscription-models'
import * as urls from '../common/urls'
export interface SubscriptionListResponse {
  paginator: Paginator<SubscriptionContract>
  bulkActions: BulkAction[]
}

export interface SubscriptionDateFilter {
  created?: string
  created_min?: string
  created_max?: string
  updated?: string
  updated_min?: string
  updated_max?: string
  paused?: string
  paused_min?: string
  paused_max?: string
  finished?: string
  finished_min?: string
  finished_max?: string
  next_order?: string
  next_order_min?: string
  next_order_max?: string
  finishing?: string
  finishing_min?: string
  finishing_max?: string
  next_attempt?: string
  next_attempt_min?: string
  next_attempt_max?: string
}

export interface SubscriptionFilter extends SubscriptionDateFilter {
  ids?: string[]
  status?: string
  pause_reason?: string
  search?: string
  customer_id?: number
  selling_plan_id?: number
  product_id?: number
  product_sku?: string
  customer_note?: string
  merchant_note?: string
  variant_id?: number
  variant_removed?: number
  currency?: string
  delivery_country_code?: string
  delivery_method?: string
  page?: number
  retrying?: string
  dunning_retries?: string
  processed_installments_min?: number
  processed_installments_max?: number
  frequency?: string
  frequency_count?: number
  prepaid_deliveries?: number
  anchors?: string
  price_type?: string
  price_min?: number
  price_max?: number
  sort?: string
}

export const useSubscriptionFilter = (): SubscriptionFilter => {
  const query = useQuery()

  const page = parseInt(query.get('page') || '1')
  const sort = query.get('sort') || undefined
  const status = query.get('status') ?? undefined
  const retrying = query.get('retrying') ?? undefined
  const dunning_retries = query.get('dunning_retries') ?? undefined
  const parseIntParam = (key: string): number | undefined => {
    const raw = query.get(key)
    if (raw === null || raw === '') return undefined
    const parsed = parseInt(raw)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  const processed_installments_min = parseIntParam('processed_installments_min')
  const processed_installments_max = parseIntParam('processed_installments_max')
  const pause_reason = query.get('pause_reason') ?? undefined
  const search = query.get('search') || undefined
  const customer_id = query.get('customer_id')
    ? parseInt(query.get('customer_id') ?? '0')
    : undefined
  const product_id = query.get('product_id') ? parseInt(query.get('product_id') ?? '0') : undefined
  const product_sku = query.get('product_sku') || undefined
  const customer_note = query.get('customer_note') || undefined
  const merchant_note = query.get('merchant_note') || undefined
  const variant_id = query.get('variant_id') ? parseInt(query.get('variant_id') ?? '0') : undefined
  const variant_removed = query.has('variant_removed') ? 1 : undefined
  const selling_plan_id = query.get('selling_plan_id')
    ? parseInt(query.get('selling_plan_id') ?? '0')
    : undefined
  const frequency = query.get('frequency') ?? undefined
  const frequency_count = query.get('frequency_count')
    ? parseInt(query.get('frequency_count') ?? '0')
    : undefined
  const prepaid_deliveries = query.get('prepaid_deliveries')
    ? parseInt(query.get('prepaid_deliveries') ?? '0')
    : undefined
  const anchors = query.get('anchors') || undefined
  const price_type = query.get('price_type') || undefined
  const price_min_raw = query.get('price_min')
  const price_min =
    price_min_raw !== null && price_min_raw !== ''
      ? Number.isFinite(parseFloat(price_min_raw))
        ? parseFloat(price_min_raw)
        : undefined
      : undefined
  const price_max_raw = query.get('price_max')
  const price_max =
    price_max_raw !== null && price_max_raw !== ''
      ? Number.isFinite(parseFloat(price_max_raw))
        ? parseFloat(price_max_raw)
        : undefined
      : undefined
  const currency = query.get('currency') || undefined
  const delivery_country_code = query.get('delivery_country_code') || undefined
  const delivery_method = query.get('delivery_method') || undefined
  const created = query.get('created') || undefined
  const created_min = query.get('created_min') || undefined
  const created_max = query.get('created_max') || undefined
  const updated = query.get('updated') || undefined
  const updated_min = query.get('updated_min') || undefined
  const updated_max = query.get('updated_max') || undefined
  const paused = query.get('paused') || undefined
  const paused_min = query.get('paused_min') || undefined
  const paused_max = query.get('paused_max') || undefined
  const finished = query.get('finished') || undefined
  const finished_min = query.get('finished_min') || undefined
  const finished_max = query.get('finished_max') || undefined
  const next_order = query.get('next_order') || undefined
  const next_order_min = query.get('next_order_min') || undefined
  const next_order_max = query.get('next_order_max') || undefined
  const finishing = query.get('finishing') || undefined
  const finishing_min = query.get('finishing_min') || undefined
  const finishing_max = query.get('finishing_max') || undefined
  const next_attempt = query.get('next_attempt') || undefined
  const next_attempt_min = query.get('next_attempt_min') || undefined
  const next_attempt_max = query.get('next_attempt_max') || undefined

  return useMemo(
    () => ({
      page,
      sort,
      status,
      retrying,
      dunning_retries,
      processed_installments_min,
      processed_installments_max,
      pause_reason,
      search,
      customer_id,
      product_id,
      product_sku,
      customer_note,
      merchant_note,
      variant_id,
      variant_removed,
      selling_plan_id,
      currency,
      delivery_country_code,
      delivery_method,
      frequency,
      frequency_count,
      prepaid_deliveries,
      anchors,
      price_type,
      price_min,
      price_max,
      created,
      created_min,
      created_max,
      updated,
      updated_min,
      updated_max,
      paused,
      paused_min,
      paused_max,
      finished,
      finished_min,
      finished_max,
      next_order,
      next_order_min,
      next_order_max,
      finishing,
      finishing_min,
      finishing_max,
      next_attempt,
      next_attempt_min,
      next_attempt_max,
    }),
    [
      page,
      sort,
      status,
      retrying,
      dunning_retries,
      processed_installments_min,
      processed_installments_max,
      pause_reason,
      search,
      customer_id,
      product_id,
      product_sku,
      customer_note,
      merchant_note,
      variant_id,
      variant_removed,
      selling_plan_id,
      currency,
      delivery_country_code,
      delivery_method,
      frequency,
      frequency_count,
      prepaid_deliveries,
      anchors,
      price_type,
      price_min,
      price_max,
      created,
      created_min,
      created_max,
      updated,
      updated_min,
      updated_max,
      paused,
      paused_min,
      paused_max,
      finished,
      finished_min,
      finished_max,
      next_order,
      next_order_min,
      next_order_max,
      finishing,
      finishing_min,
      finishing_max,
      next_attempt,
      next_attempt_min,
      next_attempt_max,
    ]
  )
}

export const fetchSubscriptionList = (_: string, filter: SubscriptionFilter) => {
  if (anyWindow.newUserTest) {
    return Promise.resolve(subscriptionListEmptyData)
  }

  return backendFetchResultData<SubscriptionListResponse>(
    'GET',
    urls.urlWithParams('/subscriptions', filter)
  )
}

export const useSubscriptionListSWR = (filter: SubscriptionFilter, polling: boolean) =>
  useSWR<SubscriptionListResponse>(['subscriptions', filter], fetchSubscriptionList, {
    refreshInterval: polling ? 7000 : undefined,
  })
